import { Head } from "@inertiajs/react";
import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Grid,
  Heading,
  Section,
  Text,
} from "@radix-ui/themes";
import { Download } from "lucide-react";
import type { SponsorsReportProps } from "@/types/sponsors_report";
import { badgeColors } from "@/shared/constants/constants";
import ImpressionsChart from "@/shared/sponsors/ImpressionsChart";
import RangePicker from "@/shared/sponsors/RangePicker";
import StatTile from "@/shared/sponsors/StatTile";
import { formatCount, formatCtr } from "@/shared/sponsors/stats";
import CreativesTable from "@/shared/tables/sponsors/CreativesTable";
import PagesTable from "@/shared/tables/sponsors/PagesTable";

// One screen a sponsor glances at weekly: how many people saw the ad, how many
// clicked, broken down by creative and by page. Everything on it comes from the
// nightly rollup, so "today" is never here — the range ends yesterday.
export default function SponsorsReport({
  sponsor,
  campaigns,
  range,
  totals,
  creatives,
  pages,
  series,
  ctr_min_impressions,
  report_path,
  pdf_path,
  token,
}: SponsorsReportProps) {
  return (
    <>
      <Head title={`${sponsor.name} · Sponsor report`} />
      <Section size="1" px="4">
        <Container size="3">
          <Flex direction="column" gap="6">
            <Box>
              <Text size="2" color="gray" as="p">
                Sponsor report
              </Text>
              <Heading size="8" className="text-xl md:text-2xl lg:text-4xl">
                {sponsor.name}
              </Heading>
              {campaigns.length > 0 && (
                <Flex direction="column" gap="1" mt="3">
                  {campaigns.map((campaign) => (
                    <Flex key={campaign.id} align="center" gap="2" wrap="wrap">
                      <Text size="2" weight="medium">
                        {campaign.name}
                      </Text>
                      <Text size="2" color="gray">
                        {campaign.starts_on} – {campaign.ends_on}
                      </Text>
                      <Badge
                        color={badgeColors[campaign.status] ?? "gray"}
                        variant="soft"
                        size="1"
                      >
                        {campaign.status}
                      </Badge>
                    </Flex>
                  ))}
                </Flex>
              )}
            </Box>

            <Flex align="start" justify="between" gap="3" wrap="wrap">
              <RangePicker
                range={range}
                reportPath={report_path}
                token={token}
              />
              {/* A plain anchor, not an Inertia Link: the PDF is a file
                  download, not a page visit. */}
              <Button asChild variant="soft" size="2">
                <a href={pdf_path}>
                  <Download size={14} />
                  Download PDF
                </a>
              </Button>
            </Flex>

            <Grid columns={{ initial: "1", sm: "3" }} gap="3">
              <StatTile
                label="Impressions"
                value={formatCount(totals.impressions)}
              />
              <StatTile label="Clicks" value={formatCount(totals.clicks)} />
              <StatTile
                label="Click-through rate"
                value={formatCtr(totals.ctr)}
                hint={
                  totals.ctr == null
                    ? `Shown once there are ${ctr_min_impressions} impressions`
                    : undefined
                }
              />
            </Grid>

            <Box>
              <Heading size="4" mb="2">
                Impressions over time
              </Heading>
              <ImpressionsChart series={series} />
            </Box>

            <Box>
              <Heading size="4" mb="2">
                By creative
              </Heading>
              {creatives.length === 0 ? (
                <Text size="2" color="gray">
                  No creatives yet.
                </Text>
              ) : (
                <CreativesTable creatives={creatives} />
              )}
            </Box>

            <Box>
              <Heading size="4" mb="2">
                By page
              </Heading>
              {pages.length === 0 ? (
                <Text size="2" color="gray">
                  No impressions in this range yet.
                </Text>
              ) : (
                <PagesTable pages={pages} />
              )}
            </Box>

            <Text size="1" color="gray" as="p">
              Updated daily · times shown in Pacific.
            </Text>
          </Flex>
        </Container>
      </Section>
    </>
  );
}
